/**
 * Share sheet.
 *
 * §99: sharing must be fast. Open it and the link already exists — expiry,
 * password and download settings are adjustments, not a wizard to complete
 * before anything happens.
 */

import { useEffect, useState } from "react";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { Input } from "../../components/ui/Input.js";
import { Switch } from "../../components/ui/Switch.js";
import { Dialog } from "../../components/ui/Dialog.js";
import { useToast } from "../../components/ui/Toast.js";
import { errorMessage, trpcClient } from "../../lib/trpc.js";
import { openFileKey } from "../../lib/keyring.js";
import { newLinkKey, sealForLink, withLinkKey } from "./linkKey.js";
import "./ShareDialog.css";

const EXPIRY_OPTIONS = [
  { label: "Never", hours: null },
  { label: "1 hour", hours: 1 },
  { label: "1 day", hours: 24 },
  { label: "7 days", hours: 24 * 7 },
  { label: "30 days", hours: 24 * 30 },
] as const;

export interface ShareDialogProps {
  groupId: string;
  fileIds: string[];
  onClose: () => void;
}

/**
 * Make one link, keys and all.
 *
 * Both the initial create and the settings change go through here, so they
 * cannot drift: a link reissued after a password change must carry keys just
 * as the first one did, and a second copy of this logic is exactly how that
 * stops being true.
 *
 * Every link gets a fresh key. Reusing the first one would mean the address
 * somebody was already given still opens the reissued link, which is the
 * opposite of what revoking-and-reissuing is for.
 */
async function createSealedLink(
  groupId: string,
  fileIds: string[],
  settings: {
    expiresInHours: number | null;
    password: string | null;
    allowDownload: boolean;
  },
): Promise<{ url: string; shareId: string }> {
  const { files } = await trpcClient.share.prepare.query({ groupId, fileIds });

  const linkKey = newLinkKey();
  const itemKeys: { fileId: string; sealedFileKey: string }[] = [];

  for (const file of files) {
    /* Nothing to re-seal for a file stored before encryption existed. */
    if (!file.wrappedFileKey) continue;
    const fileKey = await openFileKey(groupId, file.wrappedFileKey);
    itemKeys.push({
      fileId: file.id,
      sealedFileKey: await sealForLink(fileKey, linkKey),
    });
  }

  const link = await trpcClient.share.create.mutate({
    groupId,
    fileIds,
    allowOriginals: false,
    stripExif: true,
    itemKeys,
    ...settings,
  });

  /*
   * The key is appended here and nowhere else. `link.url` is what the server
   * built and therefore what the server knows; everything after the `#` is
   * added on this side and stays on this side.
   */
  return { url: withLinkKey(link.url, linkKey), shareId: link.shareId };
}

export function ShareDialog({ groupId, fileIds, onClose }: ShareDialogProps) {
  const { toast } = useToast();

  const [url, setUrl] = useState<string | null>(null);
  const [shareId, setShareId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  const [expiryIndex, setExpiryIndex] = useState(0);
  const [allowDownload, setAllowDownload] = useState(true);
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [copied, setCopied] = useState(false);

  /** Create immediately so there is a link to copy the moment this opens. */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const link = await createSealedLink(groupId, fileIds, {
          expiresInHours: null,
          password: null,
          allowDownload: true,
        });
        if (cancelled) return;
        setUrl(link.url);
        setShareId(link.shareId);
      } catch (caught) {
        if (!cancelled) setError(errorMessage(caught));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [groupId, fileIds]);

  /**
   * Changing a setting replaces the link rather than editing it.
   *
   * A link whose password is added *after* it was sent would already be in
   * someone's hands unprotected; revoking and reissuing is the honest
   * behaviour, and it is what the copy tells the user.
   */
  async function applySettings() {
    if (!shareId) return;
    setBusy(true);
    setError(null);

    try {
      await trpcClient.share.revoke.mutate({ groupId, shareId });
      const link = await createSealedLink(groupId, fileIds, {
        expiresInHours: EXPIRY_OPTIONS[expiryIndex]!.hours,
        password: usePassword && password ? password : null,
        allowDownload,
      });
      setUrl(link.url);
      setShareId(link.shareId);
      setCopied(false);
      toast("New link created. The previous one no longer works.", {
        variant: "success",
      });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast("Couldn't copy — select the link and copy it manually.", {
        variant: "danger",
      });
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Share ${fileIds.length} ${fileIds.length === 1 ? "photo" : "photos"}`}
      description="Anyone with this link can view them. No account needed."
    >
      <div className="fc-share">
        {error && (
          <div className="fc-share__error" role="alert">
            {error}
          </div>
        )}

        <div className="fc-share__link">
          <input
            className="fc-share__url"
            readOnly
            value={url ?? "Creating link…"}
            onFocus={(event) => event.currentTarget.select()}
            aria-label="Share link"
          />
          <Button variant="primary" onClick={copy} disabled={!url || busy}>
            {copied ? "Copied" : "Copy link"}
          </Button>
        </div>

        <div className="fc-share__settings">
          <Field label="Expires" htmlFor="share-expiry">
            <select
              id="share-expiry"
              className="fc-share__select"
              value={expiryIndex}
              onChange={(event) => setExpiryIndex(Number(event.target.value))}
            >
              {EXPIRY_OPTIONS.map((option, index) => (
                <option key={option.label} value={index}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>

          <div className="fc-share__toggle">
            <span>
              Allow downloads
              <small>Viewers can save a copy.</small>
            </span>
            <Switch
              checked={allowDownload}
              onCheckedChange={setAllowDownload}
              aria-label="Allow downloads"
            />
          </div>

          <div className="fc-share__toggle">
            <span>
              Password
              <small>Ask for a password before showing anything.</small>
            </span>
            <Switch
              checked={usePassword}
              onCheckedChange={setUsePassword}
              aria-label="Require a password"
            />
          </div>

          {usePassword && (
            <Field label="Link password" htmlFor="share-password">
              <Input
                id="share-password"
                type="text"
                autoComplete="off"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="At least 4 characters"
              />
            </Field>
          )}
        </div>

        <p className="fc-share__note">
          Shared photos are shown at preview quality and carry no camera or
          location data. Original files are never available through a link.
        </p>

        <div className="fc-share__actions">
          <Button variant="ghost" onClick={onClose}>
            Done
          </Button>
          <Button
            variant="secondary"
            loading={busy}
            disabled={!shareId || (usePassword && password.length < 4)}
            onClick={() => void applySettings()}
          >
            Apply and make a new link
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
